import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaBolt, FaHeart, FaShieldAlt, FaUsers, FaGlobe, FaRocket,
  FaLightbulb, FaArrowRight, FaCheck, FaStar
} from 'react-icons/fa';
import PageTransition from '../components/PageTransition';
import {
  fadeInUp, fadeInLeft, fadeInRight, scaleIn,
  staggerContainer, staggerContainerSlow, viewport,
} from '../utils/animations';

/* ─── Animated Counter ─────────────────────────────────────── */
const Counter = ({ end, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!started) return;
    let frame = 0;
    const totalFrames = 80;
    const timer = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setCount(Math.floor(eased * end));
      if (frame >= totalFrames) { setCount(end); clearInterval(timer); }
    }, 20);
    return () => clearInterval(timer);
  }, [started, end]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewport}
      transition={{ duration: 0.6 }}
      onViewportEnter={() => setStarted(true)}
    >
      <span className="text-5xl lg:text-6xl font-black text-white">
        {prefix}{count}{suffix}
      </span>
    </motion.div>
  );
};

/* ─── Values data ──────────────────────────────────────────── */
const values = [
  { icon: FaLightbulb, title: 'Innovation First',   desc: 'We push boundaries and embrace emerging technologies to craft solutions that stay ahead of the curve.' },
  { icon: FaShieldAlt, title: 'Quality Without Compromise', desc: 'Every line of code, every design decision is held to the highest standard of craftsmanship.' },
  { icon: FaHeart,     title: 'Client-Centric',     desc: 'Your success is our mission. We listen deeply, act transparently, and deliver results that matter.' },
  { icon: FaUsers,     title: 'Collaborative Spirit', desc: 'We work as an extension of your team, communicating openly and moving fast together.' },
  { icon: FaGlobe,     title: 'Global Perspective',  desc: 'Diverse teams, global clients, and cross-industry experience fuel our broad, creative thinking.' },
  { icon: FaBolt,      title: 'Velocity & Agility',  desc: 'Lean sprints, continuous delivery, and a startup mindset mean you see value early and often.' },
];

/* ─── Timeline data ────────────────────────────────────────── */
const milestones = [
  { year: '2019', title: 'Founded',         desc: 'Zynexis was founded with a bold mission: make enterprise-grade AI accessible to every business.' },
  { year: '2020', title: 'First 20 Clients', desc: 'Landed our first 20 clients across FinTech, HealthTech and SaaS in just 12 months.' },
  { year: '2021', title: 'Team Expansion',   desc: 'Grew to 25 engineers and launched our Cloud & DevOps practice to meet surging demand.' },
  { year: '2022', title: 'Series A Ready',   desc: 'Delivered 60+ projects, achieved profitability, and opened our first international office.' },
  { year: '2023', title: '100+ Projects',    desc: 'Crossed the 100-project milestone with a 97 % client-satisfaction score.' },
  { year: '2024', title: 'AI Centre',        desc: 'Launched a dedicated AI Research Centre and partnered with leading cloud providers.' },
];

const About = () => (
  <PageTransition>
    <div className="bg-transparent min-h-screen">

      {/* ── Hero ────────────────────────────────────────────── */}
      <section className="relative pt-36 pb-24 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-gray-700/10 blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="text-center max-w-4xl mx-auto"
          >
            {/* Badge */}
            <motion.div variants={fadeInUp} className="inline-flex mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 backdrop-blur-sm border border-gray-700 rounded-full badge-glow">
                <FaStar className="text-gray-400 text-xs" />
                <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Our Story</span>
              </div>
            </motion.div>

            {/* Headline */}
            <motion.h1 variants={fadeInUp} className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
              <span className="text-white">We Build</span>
              <br />
              <span className="animated-gradient-text">Digital Futures</span>
            </motion.h1>

            {/* Sub */}
            <motion.p variants={fadeInUp} className="text-gray-400 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10">
              Zynexis is a product engineering studio that partners with ambitious companies
              to design, build, and scale intelligent software, from MVP to enterprise.
            </motion.p>

            {/* CTA row */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/contact"
                className="group flex items-center gap-2 px-8 py-3.5 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-0.5 shine-hover"
              >
                Work With Us
                <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/projects"
                className="group flex items-center gap-2 px-8 py-3.5 border border-gray-700 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:text-white transition-all duration-300 hover:-translate-y-0.5"
              >
                See Our Work
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats ───────────────────────────────────────────── */}
      <section className="relative py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/0 via-gray-800/20 to-gray-900/0 pointer-events-none" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              { end: 150, suffix: '+', label: 'Projects Delivered' },
              { end: 50,  suffix: '+', label: 'Happy Clients' },
              { end: 5,   suffix: '+', label: 'Years Experience' },
              { end: 30,  suffix: '+', label: 'Team Members' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                variants={scaleIn}
                className="text-center p-8 glass-card rounded-2xl card-hover-lift shine-hover"
              >
                <Counter end={stat.end} suffix={stat.suffix} />
                <p className="text-gray-400 text-sm mt-2 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Our Story ───────────────────────────────────────── */}
      <section className="py-24 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left – text */}
            <motion.div
              variants={fadeInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="space-y-6"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900/60 border border-gray-700 rounded-full">
                <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Who We Are</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
                Engineering excellence,<br />
                <span className="animated-gradient-text">human touch</span>
              </h2>
              <p className="text-gray-400 leading-relaxed text-lg">
                We started Zynexis because great software shouldn't be locked behind sky-high agency fees
                or slow enterprise processes. We're a team of engineers, designers, and strategists who love
                the craft of building.
              </p>
              {/* Check-list */}
              <ul className="space-y-3">
                {[
                  'Senior engineers only, no outsourcing',
                  'Agile sprints with weekly demos',
                  'Full-spectrum: design → build → deploy → scale',
                  'Transparent pricing, no surprises',
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={viewport}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                    className="flex items-center gap-3 text-gray-300"
                  >
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-800 border border-gray-600 flex items-center justify-center">
                      <FaCheck className="text-gray-300 text-xs" />
                    </span>
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Right – decorative visual */}
            <motion.div
              variants={fadeInRight}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="flex flex-col items-center gap-6"
            >
              {/* Card + floating badges wrapper */}
              <div className="relative flex items-center justify-center w-full">
                {/* Outer ring */}
                <div className="absolute w-80 h-80 rounded-full border border-gray-700/30 animate-rotate-slow" />
                <div className="absolute w-64 h-64 rounded-full border border-gray-600/20 animate-rotate-reverse" />

                {/* Center card */}
                <div className="relative z-10 glass-card rounded-3xl p-10 text-center max-w-xs animate-float">
                  <div className="flex items-center justify-center mb-4">
                    <FaRocket className="text-gray-300 text-6xl" />
                  </div>
                  <h3 className="text-white font-black text-2xl mb-2">50+</h3>
                  <p className="text-gray-400 text-sm">Happy clients across 12 countries</p>
                </div>

                {/* Floating badges — sm and above only */}
                {[
                  { label: 'React & Next.js', top: '10%', left: '-5%', delay: 0 },
                  { label: 'AI / ML',         top: '65%', left: '-8%', delay: 0.3 },
                  { label: 'Cloud Native',    top: '15%', right: '-5%', delay: 0.6 },
                  { label: '97% CSAT',        top: '70%', right: '-8%', delay: 0.9 },
                ].map((b, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.7 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={viewport}
                    transition={{ delay: b.delay, duration: 0.5 }}
                    className="hidden sm:block absolute px-4 py-2 glass-card rounded-full text-xs font-semibold text-gray-300 badge-glow"
                    style={{ top: b.top, left: b.left, right: b.right }}
                  >
                    {b.label}
                  </motion.div>
                ))}
              </div>

              {/* Mobile-only badge pills row */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewport}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="sm:hidden flex flex-wrap justify-center gap-2"
              >
                {['React & Next.js', 'AI / ML', 'Cloud Native', '97% CSAT'].map((label, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 glass-card rounded-full text-xs font-semibold text-gray-300 badge-glow"
                  >
                    {label}
                  </span>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Values ──────────────────────────────────────────── */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-gray-950 to-transparent pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp} className="inline-flex mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 border border-gray-700 rounded-full">
                <FaHeart className="text-gray-400 text-xs" />
                <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">What Drives Us</span>
              </div>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-4">
              Our Core <span className="animated-gradient-text">Values</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-gray-400 max-w-xl mx-auto">
              These aren't words on a wall, they're the principles that shape every decision we make.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainerSlow}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {values.map((v, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                whileHover={{ y: -6, transition: { duration: 0.25 } }}
                className="glass-card rounded-2xl p-8 group card-hover-lift shine-hover"
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-700 to-gray-900 border border-gray-600 flex items-center justify-center mb-5 group-hover:border-gray-500 transition-colors duration-300">
                  <v.icon className="text-2xl text-gray-300 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-white font-bold text-xl mb-3">{v.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm">{v.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Timeline ────────────────────────────────────────── */}
      <section className="py-24" style={{ display: 'none' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-4">
              Our <span className="animated-gradient-text">Journey</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-gray-400 max-w-xl mx-auto">
              From a bold idea to a global studio, every year a new chapter.
            </motion.p>
          </motion.div>

          <div className="relative max-w-3xl mx-auto">
            {/* Vertical line */}
            <motion.div
              initial={{ scaleY: 0, originY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={viewport}
              transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
              className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-gray-700 via-gray-500 to-gray-800 md:-translate-x-px"
            />

            {milestones.map((m, i) => {
              const isRight = i % 2 === 0;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: isRight ? -40 : 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={viewport}
                  transition={{ delay: i * 0.1, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                  className={`relative flex items-start gap-6 mb-12 ${
                    isRight ? 'md:flex-row-reverse md:text-right' : 'md:flex-row md:text-left'
                  } md:justify-center`}
                >
                  {/* Content card */}
                  <div className={`ml-16 md:ml-0 md:w-5/12 glass-card rounded-2xl p-6 group card-hover-lift shine-hover`}>
                    <span className="text-xs font-bold tracking-widest text-gray-500 uppercase">{m.year}</span>
                    <h3 className="text-white font-bold text-lg mt-1 mb-2">{m.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{m.desc}</p>
                  </div>

                  {/* Dot on the line */}
                  <div className="absolute left-6 md:left-1/2 top-6 w-3 h-3 rounded-full bg-gray-400 border-2 border-gray-900 md:-translate-x-1.5 transform animate-pulse-glow" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────── */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/0 via-gray-800/30 to-gray-900/0 pointer-events-none" />
        <div className="spotlight w-96 h-96 top-1/2 left-1/2" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-6">
              Ready to build something<br />
              <span className="animated-gradient-text">extraordinary?</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
              Let's turn your vision into a product your users will love.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="group flex items-center gap-2 px-8 py-4 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-1 shine-hover"
              >
                Start a Project
                <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/team"
                className="group flex items-center gap-2 px-8 py-4 border border-gray-700 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:text-white transition-all duration-300 hover:-translate-y-1"
              >
                Meet the Team
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  </PageTransition>
);

export default About;

